// GroupManagement.tsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  IconButtonProps,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  styled,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Add, ExpandMore, Save, Settings, Close } from "@mui/icons-material";
import { RawNodeDatum } from "react-d3-tree";
import CustomMenu from "../../components/CustomMenu/CustomMenu";
import { axiosRequest } from "../../service/AxiosService";
import {
  CreateGroupRequestModel,
  GroupManagementInitResponse,
  GroupModel,
  GroupVersions,
} from "../../type/response/GroupManagementInitResponse";
import OrgChart from "./component/OrgChart";
import EditGroupDialog from "./component/EditGroupDialog";
import EditGroupVersionDialog from "./component/EditGroupVersionDialog";
import EditGroupChildrenDialog from "./component/EditGroupChildrenDialog";

/**
 * グループモデルリストから階層構造を取得
 */
export const createNodeData = (groupModelList: GroupModel[]) => {
  // グループIDをキーとしてマップを作成
  const groupNodeMap = new Map<string, RawNodeDatum>();
  groupModelList.forEach(({ group, oldGroup, groupUsers }) => {
    if (!groupNodeMap.has(group.id ?? "")) {
      groupNodeMap.set(group.id ?? "", {
        name: group.id ?? "",
        attributes: {
          groupName: group.name,
          oldGroupName: oldGroup?.name || "",
          groupUserNames: (groupUsers ?? [])
            .map(
              (user) =>
                `${user.lastName || ""} ${user.middleName || ""} ${
                  user.firstName || ""
                }`
            )
            .join(", "),
        },
      });
    }
  });
  // 子ノードを追加
  groupModelList.forEach(({ groupChildren }) => {
    groupChildren?.forEach((group) => {
      if (!groupNodeMap.has(group.id ?? "")) {
        groupNodeMap.set(group.id ?? "", {
          name: group.id ?? "",
          attributes: { groupName: group.name },
        });
      }
    });
  });
  // 親子関係を設定
  groupModelList.forEach(({ group, groupChildren }) => {
    const parentNode = groupNodeMap.get(group.id ?? "")!;
    if (groupChildren) {
      parentNode.children = groupChildren.map(
        ({ id }) => groupNodeMap.get(id ?? "")!
      );
    }
  });
  // ルートノード(誰からも子として参照されていないもの)
  const childrenSet = new Set<string>();
  groupModelList.forEach(({ groupChildren }) =>
    groupChildren?.forEach(({ id }) => childrenSet.add(id ?? ""))
  );
  return Array.from(groupNodeMap.values()).filter(
    (node) => !childrenSet.has(node.name)
  );
};

/**
 * ノード用アイコンボタン
 * (foreignObject内だとデフォルトでアイコンに淵が描画されるため除外設定)
 */
export const StyledIconButton = styled(IconButton)(() => ({
  "& svg": { stroke: "none" },
}));

interface ExpandMoreProps extends IconButtonProps {
  expanded: boolean;
  withStyled: boolean;
}

/**
 * Collapse展開用ボタン
 */
export const ExpandMoreButton = styled((props: ExpandMoreProps) => {
  const { expanded, withStyled, ...other } = props;
  return withStyled ? (
    <StyledIconButton {...other}>
      <ExpandMore />
    </StyledIconButton>
  ) : (
    <IconButton {...other}>
      <ExpandMore />
    </IconButton>
  );
})(({ theme }) => ({
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expanded }) => !expanded,
      style: {
        transform: "rotate(0deg)",
      },
    },
    {
      props: ({ expanded }) => !!expanded,
      style: {
        transform: "rotate(180deg)",
      },
    },
  ],
}));

/**
 * 組織体制図コンポーネント
 * @returns
 */
/**
 * グループ管理ページコンポーネント
 * - グループ・バージョン・組織図の管理機能を提供する
 * - グループの作成・編集・削除と組織ツリー表示に対応する
 */
export default function GroupManagement() {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示

  const [loading, setLoading] = useState(true); // ロード中か判定
  const [allGroupModel, setAllGroupModel] = useState<GroupModel[]>([]);
  const [groupVersionList, setGroupVersionList] = useState<GroupVersions[]>([]); // バージョンの選択肢
  const [displayVersion, setDisplayVersion] = useState<GroupVersions | null>(
    null
  ); // 表示するバージョンを管理
  const [allExpanded, setAllExpanded] = useState(true);
  const [updateGroupModelList, setUpdateGroupModelList] = useState<
    GroupModel[]
  >([]); // 更新するグループを保持
  const [isErrors, setIsErrors] = useState(false); // 更新するデータに入力エラーがあるか判定
  const [editGroupDialog, setEditGroupDialog] = useState<{
    open: boolean;
    parent: GroupModel | null;
  }>({ open: false, parent: null });
  const [editGroupChildren, setEditGroupChildren] = useState<GroupModel | null>(
    null
  );
  const [openEditGroupVersionDialog, setOpenEditGroupVersionDialog] =
    useState(false);
  const [deleteGroupModel, setDeleteGroupModel] = useState<GroupModel | null>(
    null
  ); // 削除するグループを保持
  const [showSnackbar, setShowSnackbar] = useState(false); // Snackbarの表示状態を管理

  /**
   * 選択した年度の組織図を返す
   */
  const displayData = useMemo(() => {
    return allGroupModel
      .filter((v) => v.groupVersion?.version === displayVersion?.version)
      .sort((a, b) => ((a.group.id ?? "") < (b.group.id ?? "") ? -1 : 1));
  }, [allGroupModel, displayVersion]);

  /**
   * 初期処理
   */
  const initialize = async () => {
    // 更新・削除するグループをクリア
    setUpdateGroupModelList([]);
    setDeleteGroupModel(null);
    try {
      const response = await axiosRequest<GroupManagementInitResponse>(
        "/groups/initialize",
        "GET"
      );
      if (response) {
        setAllGroupModel(response.allGroupModel);
        setGroupVersionList(
          response.allGroupVersion.sort((a, b) =>
            a.version < b.version ? -1 : 1
          )
        );

        // デフォルトで有効なバージョンを表示
        setDisplayVersion((prev) => {
          const targetList = response.allGroupVersion.filter((v) => v.isEnable);
          return !prev && targetList.length > 0 ? targetList[0] : prev;
        });

        setLoading(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  /**
   * グループモデル登録・更新・削除
   */
  const createOrUpdateGroup = async (groupModelList: GroupModel[], idempotencyKey?: string) => {
    try {
      const response = await axiosRequest<string>(
        "/groups/createOrUpdateGroup",
        "POST",
        {
          data: { groupModelList: groupModelList },
          ...(idempotencyKey ? { params: { "Idempotency-Key": idempotencyKey } } : {}),
        }
      );
      if (response) {
        initialize();
        setShowSnackbar(true);
        return response;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  };

  /**
   * グループ新規作成（新規登録専用API）
   */
  const createGroup = async (model: CreateGroupRequestModel, idempotencyKey: string) => {
    try {
      const response = await axiosRequest<string>(
        "/groups/createGroup",
        "POST",
        { data: model, params: { "Idempotency-Key": idempotencyKey } }
      );
      if (response) {
        initialize();
        setShowSnackbar(true);
        return response;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  };

  /**
   * グループ名変更
   */
  const handleUpdateGroupName = (
    groupId: string,
    newValue: string,
    error: boolean
  ) => {
    setAllGroupModel((prevList) =>
      prevList.map((prev) => {
        if (prev.group.id === groupId) {
          const newModel: GroupModel = {
            ...prev,
            group: { ...prev.group, name: newValue },
          };
          setUpdateGroupModelList((prevUpdateList) => [
            ...prevUpdateList.filter((p) => p.group.id !== groupId),
            newModel,
          ]);
          return newModel;
        } else {
          return prev;
        }
      })
    );
    setIsErrors(error);
  };

  /**
   * 確認後、グループ削除処理
   */
  const deleteGroup = async () => {
    if (deleteGroupModel) {
      const target: GroupModel = {
        ...deleteGroupModel,
        group: { ...deleteGroupModel.group, isDeleted: true },
      };
      const result = await createOrUpdateGroup([target]); // グループの論理削除フラグをtrueに設定して更新
      if (result) setDeleteGroupModel(null);
    }
  };

  /**
   * グループ削除
   */
  const handleDeleteGroup = (groupId: string) => {
    setDeleteGroupModel(() => {
      const targetList = displayData.filter((v) => v.group.id === groupId);
      return targetList.length > 0 ? targetList[0] : null;
    });
  };

  return (
    <CustomMenu>
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <CircularProgress />
        </div>
      ) : (
        <Box sx={{ p: 1 }}>
          {/* 表示するバージョン */}
          <Grid
            container
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              my: 1,
              gap: 1,
            }}
          >
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: 1,
              }}
            >
              <FormControl sx={{ width: 200 }}>
                <InputLabel id="displayVersionLabel">バージョン</InputLabel>
                <Select
                  labelId="displayVersionLabel"
                  label="バージョン"
                  value={displayVersion?.version ?? null}
                  onChange={(e) =>
                    setDisplayVersion(() => {
                      const targetList = groupVersionList.filter(
                        (v) => v.version === e.target.value
                      );
                      return targetList.length > 0 ? targetList[0] : null;
                    })
                  }
                >
                  {groupVersionList.map((v) => (
                    <MenuItem key={v.id} value={v.version}>
                      {v.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Button
                  startIcon={<Settings />}
                  onClick={() => setOpenEditGroupVersionDialog(true)}
                >
                  バージョン管理
                </Button>
              </Box>
            </Grid>
            <Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={allExpanded}
                    onChange={(e) => setAllExpanded(e.target.checked)}
                  />
                }
                label="詳細情報の全開閉"
              />
              <Button
                variant="contained"
                onClick={() => createOrUpdateGroup(updateGroupModelList)}
                startIcon={<Save />}
                disabled={updateGroupModelList.length <= 0 || isErrors}
              >
                グループ名を保存
              </Button>
            </Grid>
          </Grid>
          {/* 組織図 */}
          <Box
            sx={{
              width: "100%",
              overflowX: isMobile ? "auto" : "hidden",
              overflowY: "hidden",
            }}
          >
            <OrgChart
              displayData={displayData}
              handleUpdateGroupName={handleUpdateGroupName}
              handleDeleteGroup={handleDeleteGroup}
              handleEditGroupChildren={(parentGroupId: string) =>
                setEditGroupChildren(() => {
                  const targetList = displayData.filter(
                    (v) => v.group.id === parentGroupId
                  );
                  return targetList.length > 0 ? targetList[0] : null;
                })
              }
              allExpanded={allExpanded}
            />
          </Box>
          {/* グループ新規作成ボタン */}
          <Tooltip title="グループ新規作成" placement="left-start">
            <Fab
              color="primary"
              aria-label="グループ新規作成"
              onClick={() => setEditGroupDialog({ open: true, parent: null })}
              sx={{ position: "fixed", bottom: 48, right: 48 }}
            >
              <Add />
            </Fab>
          </Tooltip>
        </Box>
      )}
      {/* グループバージョン新規作成ダイアログ */}
      <EditGroupVersionDialog
        open={openEditGroupVersionDialog}
        onClose={(action) => {
          setOpenEditGroupVersionDialog(false);
          if (action) initialize();
        }}
        groupVersionList={groupVersionList}
      />
      {/* 子グループ追加・削除ダイアログ */}
      <EditGroupChildrenDialog
        open={!!editGroupChildren}
        onClose={() => setEditGroupChildren(null)}
        groupModel={editGroupChildren}
        groupModelList={allGroupModel}
        createOrUpdateGroup={createOrUpdateGroup}
        handleOpenEditGroupDialog={(open, parent) => {
          setEditGroupChildren(null);
          setEditGroupDialog({ open: open, parent: parent });
        }}
      />
      {/* グループ新規作成ダイアログ */}
      <EditGroupDialog
        open={editGroupDialog.open}
        onClose={() => setEditGroupDialog({ open: false, parent: null })}
        groupModelList={allGroupModel}
        groupVersionList={groupVersionList}
        displayVersion={displayVersion}
        createGroup={createGroup}
        parent={editGroupDialog.parent}
      />
      {/* グループ削除確認ダイアログ */}
      <Dialog
        open={!!deleteGroupModel}
        onClose={() => setDeleteGroupModel(null)}
      >
        <DialogTitle>グループ削除確認</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            削除するとグループ間の親子関係も解除されます。
          </Typography>
          <Typography variant="body2">(子グループは削除されません)</Typography>
          <Typography variant="body2" sx={{ mt: "1rem" }}>
            本当に削除しますか?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGroupModel(null)}>キャンセル</Button>
          <Button color="error" onClick={() => deleteGroup()}>
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 登録/更新完了メッセージ */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message="グループの登録／更新が完了しました。"
        autoHideDuration={6000}
        action={
          <IconButton
            size="small"
            aria-label="closeSnackbar"
            color="inherit"
            onClick={() => setShowSnackbar(false)}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </CustomMenu>
  );
}
